"use client";

import { ColumnDef } from "@tanstack/react-table";
import Image from "next/image";
import { twMerge } from "tailwind-merge";

import ExchangeImage from "@/components/global/exchange-swticher/exchangeIcon";
import { DataTableColumnHeader } from "@/components/ui/data-table-col-header";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  cn,
  formatNumber,
  percentageFormat,
  textColorBasedOnNumber,
} from "@/lib/utils";
import * as m from "@/paraglide/messages";

import { PortfolioAssetType } from "./types";

export const columns: ColumnDef<PortfolioAssetType>[] = [
  {
    accessorKey: "asset_name",
    meta: {
      title: m.name(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.name()}
        />
      );
    },
    cell: ({ row }) => {
      const value = row.original.current_price ?? 0;
      const formatted = formatNumber(value, true);
      return (
        <div className="">
          <div className="flex flex-row items-center gap-2 w-full">
            <Image
              src={
                row.original.asset_details?.asset_data?.image ??
                "/defaultIcon.svg"
              }
              width={24}
              height={24}
              alt={row.original.asset_details?.asset_data?.symbol ?? ""}
              className="rounded-full"
            />
            <div className="w-full">
              <div className="font-medium">
                {row.original.asset_name}{" "}
                <span className="md:inline hidden">
                  •{" "}
                  <span className="text-muted-foreground text-xs uppercase">
                    {row.original.asset_details?.asset_data?.symbol}
                  </span>
                </span>
              </div>
              <div className="text-muted-foreground gap-1 items-center w-full lg:flex hidden">
                <span className="text-xs">
                  {percentageFormat(row.original.weight as number)}%
                </span>
                <div className="h-[8px] w-16 border border-input bg-background rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{
                      width: `${
                        formatNumber(
                          row.original.weight as number,
                          false,
                        ).includes("<")
                          ? 0
                          : formatNumber(row.original.weight as number, false)
                      }%`,
                    }}
                  />
                </div>
              </div>
              <div className="font-medium text-xs text-left md:hidden inline">
                {formatted}
              </div>
            </div>
          </div>
        </div>
      );
    },
    size: 170,
  },
  {
    accessorKey: "quantity",
    meta: {
      title: m.amount_value(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.amount_value()} />;
    },
    cell: ({ row }) => {
      const amount = row.original.total_value ?? 0;
      const formatted = formatNumber(amount, true);
      return (
        <div className="flex items-center justify-end gap-1">
          <div className="font-medium text-right">
            {formatNumber(row.original.quantity as number)}{" "}
          </div>
          <span className="text-muted-foreground text-right text-xs">
            ({formatted})
          </span>
        </div>
      );
    },
    size: 170,
  },
  {
    accessorKey: "market_price",
    meta: {
      title: m.current_price(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title={m.current_price()} />
      );
    },
    cell: ({ row }) => {
      const value = row.original.market_price ?? 0;
      const formatted = formatNumber(value, true);
      return (
        <div>
          <div className="font-bold text-right">{formatted}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "avg_buy_value",
    meta: {
      title: m.avg_buy_sell(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.avg_buy_sell()} />;
    },
    cell: ({ row }) => {
      const avgBuy = row.original.avg_buy_value ?? 0;

      const buy = formatNumber(avgBuy, true);
      return (
        <div className="text-right ">
          <div className="font-medium text-sm">{buy}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "pnl.value",
    meta: {
      title: m.pnl(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.pnl()} />;
    },
    cell: ({ row }) => {
      const pnl = row.original.pnl?.value ?? 0;
      const formatted = formatNumber(pnl, true, true);
      return (
        <div className="text-left ">
          <div
            className={twMerge(
              textColorBasedOnNumber(pnl),
              "text-xs  text-right font-bold",
            )}
          >
            {formatted}
          </div>
          <div
            className={twMerge(
              textColorBasedOnNumber(pnl),
              "text-xs text-right",
            )}
          >
            {" "}
            {percentageFormat(row.original.pnl?.percent ?? 0, true)}%
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "asset_details.asset_data.price_change_percentage_24h",
    meta: {
      title: m._24hr_change(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m._24hr_change()} />;
    },
    cell: (info) => {
      const value: any = info.getValue() ?? 0;
      return (
        <div>
          <div
            className={twMerge(
              textColorBasedOnNumber(value),
              "text-right  font-bold",
            )}
          >
            {percentageFormat(value, true)}%
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "asset_details",
    meta: {
      title: m.market_cap(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.market_cap()} />;
    },
    cell: ({ row }) => {
      const marketCap = row.original.asset_details?.asset_data?.market_cap ?? 0;
      const formatted = formatNumber(marketCap, true);
      return (
        <div className="text-right ">
          <p>{formatted}</p>
        </div>
      );
    },
  },
  {
    accessorKey: "open_orders_count",
    meta: {
      title: m.pending_order(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader column={column} title={m.pending_order()} />
      );
    },
    cell: (info) => {
      const openOrders: any = info.getValue() ?? 0;
      return (
        <div className="ps-14 text-right">
          <Tooltip key={info.row.index} delayDuration={0}>
            <TooltipTrigger>
              <p>{parseFloat(openOrders.buy) + parseFloat(openOrders.sell)}</p>
            </TooltipTrigger>

            <TooltipContent side="top" className="flex items-center gap-4">
              <div className="grid gap-2">
                <p>
                  {m.buy_order_count()}{" "}
                  <>
                    <span>{formatNumber(openOrders.buy ?? 0)}</span>
                  </>
                </p>

                <p>
                  {m.sell_order_count()}{" "}
                  <>
                    <span>{formatNumber(openOrders.sell ?? 0)}</span>
                  </>
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  {
    accessorKey: "providers",
    meta: {
      title: m.exchanges(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          title={m.exchanges()}
          enableSorting={false}
        />
      );
    },
    cell: (info) => {
      const list: { provider_id: number; type: string }[] = info.getValue()
        ? [...(info.getValue() as any)]
        : [];

      return (
        <div className="items-end justify-end text-right flex gap-1.5 pr-4 ">
          {list.map((response) => {
            if (response.type === "paper")
              return (
                <Image
                  key={info.row.index}
                  src="/paper.svg"
                  alt="Paper Account Icon"
                  width={17}
                  height={17}
                />
              );
            return (
              <ExchangeImage
                key={Math.random() * 1000}
                providerId={response.provider_id}
              ></ExchangeImage>
            );
          })}
        </div>
      );
    },
  },
];

export const mobileCols: ColumnDef<PortfolioAssetType>[] = [
  {
    accessorKey: "asset_name",
    meta: {
      title: m.asset(),
    },
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          className="justify-start"
          title={m.asset()}
        />
      );
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-row items-center gap-2 w-full">
          <Image
            src={
              row.original.asset_details?.asset_data?.image ??
              "/defaultIcon.svg"
            }
            width={20}
            height={20}
            alt={row.original.asset_details?.asset_data?.symbol ?? ""}
          />
          <div className="w-full font-bold">
            <span className="text-ellipsis whitespace-nowrap text-sm">
              {`${
                row.original.asset_details?.asset_data?.symbol?.toUpperCase() ??
                row.original.asset_name?.toUpperCase()
              } `}
              {/* ${formatNumber(
                row.original.quantity as number,
              )} */}
            </span>
            <div className="text-xs text-left font-medium">
              {formatNumber(row.original.quantity as number)}{" "}
              {/* {row.original.asset_details?.asset_data?.symbol?.toUpperCase()} */}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "pnl.value",
    meta: {
      title: "PnL",
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title="PnL" />;
    },
    cell: ({ row }) => {
      const pnl = row.original.pnl?.value ?? 0;
      const formatted = formatNumber(pnl, true, true);
      return (
        <div className="text-right">
          <div
            className={twMerge(
              textColorBasedOnNumber(pnl),
              "text-xs font-bold",
            )}
          >
            {formatted}
          </div>
          <div className={twMerge(textColorBasedOnNumber(pnl), "text-xs")}>
            {" "}
            {percentageFormat(row.original.pnl?.percent ?? 0, true)}%
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "market_price",
    meta: {
      title: m.price(),
    },
    header: ({ column }) => {
      return <DataTableColumnHeader column={column} title={m.price()} />;
    },
    cell: ({ row }) => {
      const value = row.original.market_price ?? 0;
      const formatted = formatNumber(value, true);
      const _24h =
        row.original.asset_details?.asset_data?.price_change_percentage_24h ??
        0;
      return (
        <div className="text-sm text-right">
          <p className="font-bold whitespace-nowrap">{formatted}</p>
          <p
            className={cn(textColorBasedOnNumber(_24h), "text-xs font-medium")}
          >
            {percentageFormat(_24h, true)}%
          </p>
        </div>
      );
    },
  },
];

export const hiddenColumns = () => ({
  avg_buy_value: false,
  pnl_value: false,
  asset_details: false,
  open_orders_count: false,
});
